/** UN Comtrade HS 851762 destination map for the Starlink revenue view. */

import fallback from '@/data/valueChain/starlinkComtradeMap.json';

export type ComtradePartner = {
  partner: string;
  iso3: string;
  region: string;
  labeled: boolean;
  values: Record<string, number>;
  quantities: Record<string, number>;
  quantity_unit: string | null;
};

export type ComtradeMapPayload = {
  source: string;
  hs_code: string;
  reporter: string;
  reporter_iso3: string;
  reporter_code: string;
  flow: string;
  years: string[];
  world: Record<string, number>;
  partner_count: number;
  partners: ComtradePartner[];
  caveat: string;
};

export type ComtradeTile = {
  id: string;
  label: string;
  iso3: string | null;
  value: number;
  growthPct: number | null;
  labeled: boolean;
  quantity: number | null;
};

export type ComtradeRegionGroup = {
  id: string;
  label: string;
  revenue: number;
  tiles: ComtradeTile[];
};

export const COMTRADE_FALLBACK = fallback as ComtradeMapPayload;

export const COMTRADE_REGION_ORDER = [
  'North America',
  'Europe',
  'Asia',
  'Latin America',
  'Middle East',
  'Oceania',
  'Africa',
  'Other',
] as const;

const TILES_PER_REGION = 6;

export function partnerValue(partner: ComtradePartner, year: string): number {
  return Number(partner.values[year] || 0);
}

export function growthPct(partner: ComtradePartner, year: string, baseYear = '2022'): number | null {
  const base = partnerValue(partner, baseYear);
  const current = partnerValue(partner, year);
  if (base <= 0 || year === baseYear) return null;
  return ((current / base) - 1) * 100;
}

export function worldTotal(payload: ComtradeMapPayload, year: string): number {
  const official = Number(payload.world[year] || 0);
  if (official > 0) return official;
  return payload.partners.reduce((sum, partner) => sum + partnerValue(partner, year), 0);
}

export function groupComtradeRegions(
  payload: ComtradeMapPayload,
  year: string,
): ComtradeRegionGroup[] {
  const buckets = new Map<string, ComtradePartner[]>();
  payload.partners.forEach((partner) => {
    const region = partner.region || 'Other';
    const list = buckets.get(region) ?? [];
    list.push(partner);
    buckets.set(region, list);
  });

  return COMTRADE_REGION_ORDER.flatMap((region) => {
    const members = (buckets.get(region) ?? [])
      .map((partner) => ({ partner, value: partnerValue(partner, year) }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
    if (!members.length) return [];
    const revenue = members.reduce((sum, item) => sum + item.value, 0);
    const labeled = members.filter((item) => item.partner.labeled);
    const unlabeled = members.filter((item) => !item.partner.labeled);
    const keep = new Set(
      [...labeled, ...unlabeled.slice(0, Math.max(0, TILES_PER_REGION - labeled.length))].map(
        (item) => item.partner.iso3,
      ),
    );
    const head = members.filter((item) => keep.has(item.partner.iso3));
    const overflow = members.filter((item) => !keep.has(item.partner.iso3));
    const tiles: ComtradeTile[] = head.map(({ partner, value }) => ({
      id: partner.iso3,
      label: shortPartnerName(partner.partner),
      iso3: partner.iso3,
      value,
      growthPct: growthPct(partner, year),
      labeled: partner.labeled,
      quantity: partner.quantities[year] ?? null,
    }));
    if (overflow.length) {
      const otherValue = overflow.reduce((sum, item) => sum + item.value, 0);
      tiles.push({
        id: `${region}-other`,
        label: `Other (${overflow.length})`,
        iso3: null,
        value: otherValue,
        growthPct: null,
        labeled: overflow.some((item) => item.partner.labeled),
        quantity: null,
      });
    }
    return [{ id: region, label: region, revenue, tiles }];
  });
}

export function shortPartnerName(name: string): string {
  return name
    .replace('United Kingdom', 'UK')
    .replace('United Arab Emirates', 'UAE')
    .replace('Rep. of Korea', 'South Korea')
    .replace('China, Hong Kong SAR', 'Hong Kong')
    .replace('China, Macao SAR', 'Macao')
    .replace('Bolivia (Plurinational State of)', 'Bolivia')
    .replace('United Rep. of Tanzania', 'Tanzania')
    .replace('Dem. Rep. of the Congo', 'DR Congo')
    .replace('Lao People\'s Dem. Rep.', 'Laos')
    .replace('Viet Nam', 'Vietnam')
    .replace('Türkiye', 'Turkey');
}
